import { Link, useNavigate } from "react-router-dom";
import { ChevronRight } from "lucide-react";
import { Navbar } from "../../components/Navbar";
import { Footer } from "../../components/Footer";
import { Button } from "../../components/ui/button";
import { useAuth } from "../../context/AuthContext";
import { useSeo, SITE_URL } from "../../lib/seo";

// Public, indexable guide pages (marketing/informational only). Never rendered inside the app, never as a modal.
export function SeoPageShell({ path, title, description, h1, intro, faqs, children, testId, cta }) {
  const navigate = useNavigate();
  const { user } = useAuth();
  const jsonLd = [
    { "@context": "https://schema.org", "@type": "BreadcrumbList", itemListElement: [
      { "@type": "ListItem", position: 1, name: "Home", item: `${SITE_URL}/` },
      { "@type": "ListItem", position: 2, name: h1, item: `${SITE_URL}${path}` }] },
    ...(faqs?.length ? [{ "@context": "https://schema.org", "@type": "FAQPage", mainEntity: faqs.map(([q, a]) => ({ "@type": "Question", name: q, acceptedAnswer: { "@type": "Answer", text: a } })) }] : []),
  ];
  useSeo({ title, description, path, jsonLd });
  return (
    <div className="min-h-screen bg-slate-50 flex flex-col" data-testid={testId}>
      <Navbar />
      <main className="flex-1 max-w-3xl w-full mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <nav className="text-xs text-slate-500 flex items-center gap-1" aria-label="Breadcrumb"><Link to="/" className="hover:text-primary">Home</Link><ChevronRight className="w-3 h-3" /><span className="text-slate-800">{h1}</span></nav>
        <p className="text-[11px] font-bold uppercase tracking-[0.2em] text-primary mt-6">Guide</p>
        <h1 className="font-heading text-4xl sm:text-5xl font-bold text-slate-900 tracking-tight mt-1" data-testid="seo-page-h1">{h1}</h1>
        <p className="mt-4 text-lg text-slate-600 leading-relaxed">{intro}</p>
        <article className="mt-8 space-y-8 text-slate-700 leading-relaxed [&_h2]:font-heading [&_h2]:text-2xl [&_h2]:font-bold [&_h2]:text-slate-900 [&_h2]:tracking-tight [&_h3]:font-heading [&_h3]:text-lg [&_h3]:font-bold [&_h3]:text-slate-900 [&_h3]:mt-5 [&_p]:mt-3 [&_ul]:mt-3 [&_ul]:space-y-2 [&_ul]:list-disc [&_ul]:pl-5 [&_ol]:mt-3 [&_ol]:space-y-2 [&_ol]:list-decimal [&_ol]:pl-5 [&_a]:text-primary [&_a]:font-semibold [&_a]:underline [&_a]:underline-offset-2">
          {children}
          {faqs?.length > 0 && (
            <section data-testid="seo-page-faq">
              <h2>Frequently asked questions</h2>
              <div className="mt-3 divide-y divide-slate-200 border border-slate-200 rounded-2xl bg-white">
                {faqs.map(([q, a]) => <div key={q} className="p-4"><p className="font-semibold text-slate-900 !mt-0">{q}</p><p className="text-sm">{a}</p></div>)}
              </div>
            </section>
          )}
        </article>
        <aside className="mt-10 bg-slate-950 text-white rounded-2xl p-6 sm:p-8" data-testid="seo-page-cta">
          <p className="text-[11px] font-bold uppercase tracking-wider text-amber-400">Continue with First Step</p>
          <p className="mt-2 text-slate-200">{cta || "Create a free account to start studying, build your Career Road Map, and take your first full mock oral board free. Unlimited boards with detailed reports from the spoken three-person panel are part of Premium."}</p>
          <div className="mt-5 flex flex-col sm:flex-row gap-3">
            <Button onClick={() => navigate(user ? "/dashboard" : "/auth?mode=register")} className="bg-primary hover:bg-primary/90" data-testid="seo-page-cta-primary">{user ? "Go to your dashboard" : "Create your free account"}</Button>
            <Link to="/pricing" className="inline-flex items-center justify-center text-sm font-semibold text-slate-200 hover:text-white underline underline-offset-2" data-testid="seo-page-cta-pricing">See Premium pricing</Link>
          </div>
        </aside>
      </main>
      <Footer />
    </div>
  );
}
